import { Box } from '@startuptools/ui/Box';
import { Button } from '@startuptools/ui/Button';
import { Card } from '@startuptools/ui/Card';
import { CardContent } from '@startuptools/ui/CardContent';
import { IconButton } from '@startuptools/ui/IconButton';
import { Stack } from '@startuptools/ui/Stack';
import { Tooltip } from '@startuptools/ui/Tooltip';
import { Typography } from '@startuptools/ui/Typography';
import { GenericError } from '@startuptools/ui/Error';
import { NumberFormat } from '@startuptools/ui/NumberFormat';
import { Trans, useLingui } from '@lingui/react';
import { apiClient } from '../../common/http';
import { type GqlCapTable } from '../../graphql/react-operations';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { DownloadFile } from '../../common/DownloadFile';
import { ConfirmDialog } from '@startuptools/ui/ConfirmDialog';
import { AddIdentity } from '@startuptools/business-ui/identity/add/AddIdentity';
import { JuridicalKind } from '../../models/identity.model';
import { InputState } from '@startuptools/business-ui/identity/types';
import { EditCapTable } from './EditCapTable';
import { ActionMenu, ActionMenuItem } from './ui/ActionMenu';
import { useCapTableId } from './hooks/useCapTableId';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import DownloadIcon from '@mui/icons-material/Download';
import { useState } from 'react';
import { CapTablesDocument, useDeleteCapTableMutation, useDuplicateCapTableMutation } from './cap-table.graphql';
import { InvestmentRoundDialog } from './investment-round/Dialog';
import { GqlCreateIdentityInput } from '../../graphql/base-types.graphql';
import { DateFormat } from '@startuptools/ui/DateFormat';
import { useNavigate } from '../../components/Navigate';

export const Actions = ({
  enableExport,
  capTable,
  onIdentityCreated,
  refresh,
}: {
  enableExport: boolean;
  capTable: Omit<GqlCapTable, 'capTableStakes' | 'company' | 'stakeholders'>;
  onIdentityCreated: (identity: GqlCreateIdentityInput) => void;
  refresh: () => void;
}) => {
  // eslint-disable-next-line sut/loading-mutation
  const [duplicate] = useDuplicateCapTableMutation({ refetchQueries: [CapTablesDocument] });
  const companyId = useCompanyId();
  const capTableId = useCapTableId();
  const { navigate } = useNavigate();
  const { i18n } = useLingui();
  const [editCapTable, setEditCapTable] = useState(false);

  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  // eslint-disable-next-line sut/loading-mutation
  const [deleteCapTable, { error: deleteCapTableError, reset: resetDeleteCapTable }] = useDeleteCapTableMutation({
    refetchQueries: [CapTablesDocument],
  });
  const [showCreateStakeFlow, setShowCreateStakeFlow] = useState(false);
  const [showInvestmentFlow, setShowInvestmentFlow] = useState(false);

  return (
    <>
      {showCreateStakeFlow && (
        <AddIdentity
          headline={<Trans id="Add owner" />}
          juridicalKinds={[JuridicalKind.Person, JuridicalKind.Company]}
          availableInputStates={{
            [InputState.System]: true,
            [InputState.CompanyBoard]: false,
            [InputState.Swedish]: true,
            [InputState.Foreign]: true,
          }}
          defaultInputState={InputState.Swedish}
          detailFieldsSettings={{}}
          handleCreate={async function (identity) {
            setShowCreateStakeFlow(false);
            onIdentityCreated(identity);
          }}
          onClose={() => {
            setShowCreateStakeFlow(false);
          }}
        />
      )}
      {showInvestmentFlow && <InvestmentRoundDialog close={() => setShowInvestmentFlow(false)} capTable={capTable} />}
      {editCapTable && <EditCapTable id={capTableId} close={() => setEditCapTable(false)} />}
      {blobUrl && (
        <DownloadFile
          url={blobUrl}
          fileName={`captable-${capTable.displayName}.xlsx`}
          onClose={() => {
            window.URL.revokeObjectURL(blobUrl);
            setBlobUrl(null);
          }}
        />
      )}

      {confirmDelete && (
        <ConfirmDialog
          header={<Trans id="Do you really want to delete this CapTable?" />}
          confirmProps={{ children: <Trans id="Delete" /> }}
          onCancel={() => {
            resetDeleteCapTable();
            setConfirmDelete(null);
          }}
          onConfirm={async () => {
            await deleteCapTable({ variables: { companyId, input: confirmDelete } });
            await navigate(`/cap-tables`);
            setConfirmDelete(null);
          }}
        >
          {deleteCapTableError && <GenericError />}
        </ConfirmDialog>
      )}

      <Card sx={{ minWidth: 200, maxWidth: 350, position: 'relative' }}>
        <CardContent>
          <Stack direction="row" gap={2}>
            <Stack direction="column">
              <Typography sx={{ fontSize: 14 }} level="body-xs" gutterBottom>
                <Trans id="Price per share" />
              </Typography>
              <Typography level="title-sm">
                {capTable.pricePerShare ? (
                  <NumberFormat locale={i18n.locale}>{capTable.pricePerShare}</NumberFormat>
                ) : (
                  <Trans id="None" />
                )}
              </Typography>
            </Stack>
            <Stack direction="column">
              <Typography sx={{ fontSize: 14 }} gutterBottom>
                <Trans id="Fully diluted" />
              </Typography>
              <Typography level="title-sm">{capTable.fullyDiluted ? <Trans id="Yes" /> : <Trans id="No" />}</Typography>
            </Stack>
            {!capTable.fullyDiluted && (
              <Stack direction="column">
                <Typography sx={{ fontSize: 14 }} gutterBottom>
                  <Trans id="Date" />
                </Typography>
                <Typography level="title-sm">
                  {capTable.recordDate ? (
                    <DateFormat locale={i18n.locale}>{capTable.recordDate}</DateFormat>
                  ) : (
                    <Trans id="Today" />
                  )}
                </Typography>
              </Stack>
            )}

            <Box position="absolute" top="0" right="0">
              <IconButton
                color="primary"
                onClick={() => {
                  setEditCapTable(true);
                }}
                aria-label={i18n._('Edit')}
              >
                <EditIcon />
              </IconButton>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Stack gap={1} direction="row" justifyContent="space-between">
        <Stack gap={1} direction="row">
          <Button startDecorator={<AddIcon />} onClick={() => setShowCreateStakeFlow(true)}>
            <Trans id="Add owner" />
          </Button>

          <Button
            startDecorator={<AddIcon />}
            onClick={() => {
              setShowInvestmentFlow(true);
            }}
          >
            <Trans id="Add investment round" />
          </Button>
        </Stack>

        <Stack direction="row">
          <Tooltip title={i18n._('Delete')}>
            <IconButton
              aria-label={i18n._('Delete')}
              color="primary"
              onClick={() => {
                setConfirmDelete(capTableId);
              }}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title={i18n._('Export')}>
            <IconButton
              color="primary"
              aria-label={i18n._('Export')}
              disabled={!enableExport}
              onClick={async () => {
                const url = await apiClient.getCapTableExcelExportUrl(companyId, capTableId);
                setBlobUrl(url);
              }}
            >
              <DownloadIcon />
            </IconButton>
          </Tooltip>

          <ActionMenu>
            <ActionMenuItem
              icon={<FileCopyIcon />}
              action={async () => {
                const res = await duplicate({ variables: { companyId, input: capTableId } });
                if (res.data) {
                  await navigate(`/cap-tables/${res.data.duplicateCapTable.id}`);
                }
              }}
              label={<Trans id="Duplicate" />}
            />
            <ActionMenuItem
              icon={<EditIcon />}
              action={() => {
                setEditCapTable(true);
              }}
              label={<Trans id="Edit" />}
            />

            <ActionMenuItem
              icon={<DeleteIcon />}
              action={() => {
                setConfirmDelete(capTableId);
              }}
              label={<Trans id="Delete" />}
            />

            <ActionMenuItem icon={<DeleteIcon />} action={refresh} label={<Trans id="Force refresh" />} />

            <ActionMenuItem
              disabled={!enableExport}
              icon={<DownloadIcon />}
              action={async () => {
                const url = await apiClient.getCapTableExcelExportUrl(companyId, capTableId);
                setBlobUrl(url);
              }}
              label={<Trans id="Export" />}
            />
          </ActionMenu>
        </Stack>
      </Stack>
    </>
  );
};
